/**
 * The synergy panel (TRD-09 §8) — one row per active class, down the left of the
 * battlefield per the §5 layout.
 *
 * Row order comes from the sim's sort (TRD-08 R-08-14) and is never touched here: the
 * player's rows must not reorder under them mid-build (R-09-15), and the sim's stable
 * tier-then-count-then-alphabet sort is precisely what guarantees that.
 *
 * Hovering a row is the primary board-reading tool (R-09-17): every contributing tower
 * lights up with its range circle, because on a board of identical shapes this is how
 * "which ones are my Frosts?" gets answered at all.
 */
import { useState } from 'react';
import { CLASS_COLORS, classes, getClass } from '@prism/content';
import type { ClassId, UiSnapshot } from '@prism/sim';
import { countLabel, inactiveClasses, pipsFor, tierLines } from './synergyPanelModel.js';

interface SynergyPanelProps {
  synergies: UiSnapshot['synergies'];
  /** null clears the highlight. Wired to `Renderer.setClassHighlight` (R-03-24). */
  onHoverClass: (classId: ClassId | null) => void;
  /**
   * Folded state, persisted in Settings (playtest request). Open is the default and stays
   * the default — the panel is how a board is read, so it earns the space until someone
   * decides otherwise, and that decision should outlive the run they made it in.
   */
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The tier text for one class. Rendered identically whether or not the class is on the
 * board — an inactive class simply has no active tier, so every line reads as unmet.
 */
function TierDetail({ classId, activeTier }: { classId: ClassId; activeTier: 0 | 2 | 4 | 6 }) {
  const def = getClass(classId);
  const colors = CLASS_COLORS[classId];
  return (
    <div className="synergy-tiers">
      {/* The authored one-liner for what the class is FOR. It exists in content and was
          never shown anywhere — which is most of why the tiers read as a list of numbers
          rather than as a plan. */}
      <p className="synergy-blurb">{def.description}</p>
      {tierLines(def.tiers, activeTier).map((tier) => (
        <p
          key={tier.threshold}
          className={tier.active ? 'synergy-tier--active' : 'synergy-tier--inactive'}
        >
          <b style={tier.active ? { color: colors.color } : undefined}>({tier.threshold})</b>{' '}
          {tier.summary}
        </p>
      ))}
    </div>
  );
}

export function SynergyPanel({
  synergies,
  onHoverClass,
  open,
  onOpenChange,
}: SynergyPanelProps) {
  const [expanded, setExpanded] = useState<ClassId | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  // Rows only for classes actually on the board (R-09-15). The sim reports all ten —
  // count 0 included — and ten always-present rows is exactly the noise R-09-19 bans.
  const active = synergies.filter((st) => st.count > 0);
  const inactive = inactiveClasses(
    classes.map((c) => c.id),
    synergies,
  );

  return (
    <aside className="synergy-panel" onPointerLeave={() => onHoverClass(null)}>
      <button
        type="button"
        className="synergy-panel__head"
        aria-expanded={open}
        title={open ? 'Fold the synergy panel away' : 'Show synergies'}
        onClick={() => onOpenChange(!open)}
      >
        <span className="label">Synergies</span>
        {/* Folded, the count is the whole panel: it has to say what is being hidden, or
            folding it away means losing track of the board rather than tidying it. */}
        {!open && active.length > 0 && (
          <span className="muted synergy-panel__folded">
            {active.length} active
          </span>
        )}
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
      </button>

      {open && active.length === 0 && <span className="muted">Place a tower to start one.</span>}

      {open &&
        active.map((status) => {
        const def = getClass(status.classId);
        const colors = CLASS_COLORS[status.classId];
        const open = expanded === status.classId;
        return (
          <div key={status.classId} className="synergy-row-wrap">
            <button
              type="button"
              className="synergy-row"
              onPointerEnter={() => onHoverClass(status.classId)}
              onClick={() => setExpanded(open ? null : status.classId)}
              title={`${def.name} — click for tier details`}
            >
              <span className="synergy-row__swatch" style={{ background: colors.color }} />
              <b style={{ color: colors.color }}>{def.glyph}</b>
              <span className="synergy-row__name">{def.name}</span>
              <span className="muted">{countLabel(status)}</span>
              <span className="synergy-row__pips">
                {pipsFor(status).map((pip, i) => (
                  <span
                    key={i}
                    className={[
                      'pip',
                      pip.filled ? 'pip--filled' : '',
                      pip.isThreshold ? 'pip--threshold' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    style={
                      pip.active
                        ? { background: colors.color, borderColor: colors.color }
                        : undefined
                    }
                  />
                ))}
              </span>
            </button>

            {open && <TierDetail classId={status.classId} activeTier={status.activeTier} />}
          </div>
        );
      })}

      {/* Ten classes at count 0 as full rows is noise (R-09-19); one line is a map. */}
      {open && inactive.length > 0 && (
        <div className="synergy-inactive">
          <button
            type="button"
            className="synergy-inactive__toggle"
            onClick={() => setShowInactive(!showInactive)}
          >
            {showInactive ? '▾' : '▸'} {inactive.length} not active
          </button>
          {showInactive && (
            <div className="synergy-inactive__list">
              {inactive.map((id) => {
                const def = getClass(id);
                const colors = CLASS_COLORS[id];
                const open = expanded === id;
                return (
                  <div key={id}>
                    <button
                      type="button"
                      className="synergy-inactive__row"
                      onClick={() => setExpanded(open ? null : id)}
                      title={`${def.name} — click for tier details`}
                    >
                      <span aria-hidden="true">{open ? '▾' : '▸'}</span>
                      <b style={{ color: colors.color }}>{def.glyph}</b> {def.name}
                    </button>
                    {/* activeTier 0: every threshold reads as unmet, which is the truth
                        for a class with nothing on the board. */}
                    {open && <TierDetail classId={id} activeTier={0} />}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </aside>
  );
}
